import React, { useState, useMemo, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import dayjs from "dayjs";

import SegmentToolbar from "../../components/Channel/SegmentToolbar";
import SegmentPresetBar from "../../components/Channel/SegmentPresetBar";
import CustomWeekdaySelector from "../../components/Channel/CustomWeekdaySelector";
import CustomEpgSelector from "../../components/Channel/CustomEpgSelector";
import { SegmentBarChart } from "../../components/charts3";

import { useDaysSorted } from "../../hooks/useDaysSorted";
import { useAvgActiveSeries } from "../../hooks/useAvgActiveSeries";
import { useEpgData } from "../../hooks/useEpgData";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { useSegmentPresets } from "../../hooks/useSegmentPresets";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";
import { programEndClock } from "../../utils/epgTime";

/* =========================
   COLORS
========================= */

const COLOR_PALETTE = [
  "#FFB000", // Monday
  "#005B96", // Tuesday
  "#4F6BED", // Wednesday
  "#9B59B6", // Thursday
  "#D45087", // Friday
  "#FF4D6D", // Saturday
  "#FF7A00", // Sunday
];

const WEEKDAY_INDEX = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
};

export default function ChannelSegmentAnalysis({ channelId, rawVideo, loading }) {

  const storedUser = useMemo(
    () => JSON.parse(localStorage.getItem("adm_panel_user") || "{}"),
    []
  );
  const isDark = !!storedUser.darkmode;
  const today = useMemo(() => dayjs(), []);

  const [interval, setInterval] = useState("Weekly");
  const [mode, setMode] = useState("week");

  // Independent date state for this chart
  const [endDate, setEndDate] = useState(today.format("YYYY-MM-DD"));

  // Where an interval starts out. It is only a starting point: the picker owns
  // the start date from then on, which it did not before — the start was
  // recomputed from the end on every render, so a range picked by hand was
  // thrown away and the chart kept answering for the last thirty days.
  const defaultStartFor = (which, end) => {
    const e = dayjs(end, "YYYY-MM-DD");
    if (which === "Daily") return e.subtract(7, "day").format("YYYY-MM-DD");
    return e.subtract(30, "day").format("YYYY-MM-DD");
  };

  const [startDate, setStartDate] = useState(() =>
    defaultStartFor("Weekly", today.format("YYYY-MM-DD"))
  );

  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  function handleIntervalChange(val) {
    const safe = val || "Weekly";
    setInterval(safe);
    setStartDate(defaultStartFor(safe, endDate));

    if (safe === "Weekly") setMode("week");
    else if (safe === "Daily") setMode("day");
    else if (safe === "Custom") setMode("custom");
  }

  const {
    enabled,
    setEnabled,
    intervalMin,
    setIntervalMin,
    countdown,
    refreshKey,
    manualRefresh,
  } = useAutoRefresh(1);

  const [chartLoading, setChartLoading] = useState(false);
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [selectedEpg, setSelectedEpg] = useState(null);

  const {
    presets,
    loadPresets,
    savePreset,
    deletePreset,
  } = useSegmentPresets(storedUser.id, channelId);

  const [selectedPreset, setSelectedPreset] = useState(null);

  useEffect(() => { loadPresets(); }, [loadPresets]);

  useEffect(() => {
    if (!selectedPreset) return;

    setInterval(selectedPreset.interval || "Custom");
    setStartTime(selectedPreset.startMinute || "00:00");
    setEndTime(selectedPreset.endMinute || "23:59");

    const parsedDays =
      Array.isArray(selectedPreset.day)
        ? selectedPreset.day
        : typeof selectedPreset.day === "string"
          ? selectedPreset.day.split("-")
          : [];

    setSelectedWeekdays(parsedDays);

    if (selectedPreset.interval === "Weekly") setMode("week");
    else if (selectedPreset.interval === "Daily") setMode("day");
    else setMode("custom");

    setChartLoading(true);
  }, [selectedPreset]);

  function collectPresetState() {
    return { startTime, endTime, interval, selectedWeekdays };
  }

  const sortedKeys = useDaysSorted(rawVideo);
  const { epgBlocks } = useEpgData(channelId, null, {
  startDateRange: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  endDateRange: today.format("YYYY-MM-DD"),
  });

  useEffect(() => {
    if (!loading && rawVideo) setChartLoading(false);
  }, [loading, rawVideo]);

  const uniqueEpgList = useMemo(() => {
    if (!epgBlocks?.length) return [];
    const map = new Map();

    epgBlocks.forEach(item => {
      const sh = String(Math.floor(item.startMinute / 60)).padStart(2, "0");
      const sm = String(item.startMinute % 60).padStart(2, "0");
      const key = `${item.programName}-${item.weekdayInt}-${sh}:${sm}`;
      if (!map.has(key)) map.set(key, item);
    });

    return [...map.values()].sort((a, b) =>
      a.programName.localeCompare(b.programName)
    );
  }, [epgBlocks]);

  const rawSeries = useAvgActiveSeries({
    rawVideo,
    sortedVideoKeys: sortedKeys,
    startDateInput: startDate,
    endDateInput: endDate,
    startTime,
    endTime,
    mode,
    selectedWeekdays: interval === "Custom" ? selectedWeekdays : undefined,
    useTimeWindow: true,
  });

  const series = useMemo(() => {
    if (interval === "Custom" && selectedWeekdays.length === 0) return [];
    if (interval !== "Custom") return rawSeries;

    return rawSeries.map(item => {
      const idx = WEEKDAY_INDEX[item.weekday];
      return {
        ...item,
        color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
      };
    });
  }, [rawSeries, interval, selectedWeekdays]);

  const dateRangeLabel =
    `${dayjs(startDate).format("DD.MM.YYYY")} – ${dayjs(endDate).format("DD.MM.YYYY")}`;

  const { logPageChange } = useUserJourneyLogger({ channelId });

  useEffect(() => {
    logPageChange({ chartType: "segmentanalysis" });
  }, [logPageChange]);

  return (
    <Box
      p={2}
      sx={{
        overflow: "visible",
        position: "relative",
        zIndex: 0
      }}
    >

      <SegmentPresetBar
        presets={presets}
        selectedPreset={selectedPreset}
        onSelect={(name) => {
          if (name === "none") setSelectedPreset(null);
          else setSelectedPreset(presets.find(p => p.name === name));
        }}
        onSave={(name) => savePreset(name, collectPresetState())}
        onDelete={deletePreset}
      />

      <SegmentToolbar
        channelId={channelId}
        interval={interval}
        onIntervalChange={handleIntervalChange}
        mode={mode}
        startDateInput={startDate}
        endDateInput={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        refreshEnabled={enabled}
        onRefreshToggle={setEnabled}
        refreshIntervalMin={intervalMin}
        onRefreshIntervalChange={setIntervalMin}
        countdown={countdown}
        onManualRefresh={manualRefresh}
      />

      {interval === "Custom" && (
        <Box
          sx={{
            mt: 1,
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: isDark ? "#0f172a" : "#ffffff",
            position: "relative",
            zIndex: 5,
            overflow: "visible"
          }}
        >
          <CustomWeekdaySelector
            selectedDays={selectedWeekdays}
            onChange={setSelectedWeekdays}
            dayColors={Object.fromEntries(
              Object.keys(WEEKDAY_INDEX).map(day => [day, COLOR_PALETTE[WEEKDAY_INDEX[day]]])
            )}
          />

          <CustomEpgSelector
            epgList={uniqueEpgList.map(block => ({
              id: block.id,
              program_name: block.programName,
              weekday: block.weekdayInt
                ? ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"][block.weekdayInt - 1]
                : "",
              start_time: block.startMinute != null
                ? `${String(Math.floor(block.startMinute / 60)).padStart(2, "0")}:${String(block.startMinute % 60).padStart(2, "0")}`
                : "",
              end_time: block.endMinute != null ? programEndClock(block) : "",
            }))}
            selectedProgram={selectedEpg}
            onChange={(program) => {
              setSelectedEpg(program);
              if (program) {
                setSelectedWeekdays([program.weekday]);
                setStartTime(program.start_time);
                setEndTime(program.end_time);
              }
            }}
          />
        </Box>
      )}

      <Box
        position="relative"
        minHeight={420}
        sx={{
          zIndex: 0,
          marginTop: 4,
          isolation: "isolate"
        }}
      >

        {chartLoading && (
          <Box
            position="absolute"
            inset={0}
            zIndex={10}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress size={38} />
          </Box>
        )}

        <SegmentBarChart
          series={series}
          chartType="bar"
          title={`Comparison (${interval})`}
          subtitle={dateRangeLabel}
          usePointColors={interval === "Custom"}
          refreshKey={refreshKey}
        />
      </Box>
    </Box>
  );
}
